import { useCallback, useMemo, useState } from 'react'
import SummaryDashboard from '../components/snoop/SummaryDashboard'
import { fetchPgpSongMappings } from '../api/pgpClient'
import { PLACEHOLDER_GENIUS_SONG_MAPPING } from '../utils/snoop/placeholderColumns'

const VIEW_OPTIONS = [
  { value: 'unresolved', label: 'Unmatched' },
  { value: 'matched', label: 'Matched' },
  { value: 'all', label: 'All' },
]

const VIEW_TITLES = {
  unresolved: 'Chart songs that have not matched a Genius song',
  matched: 'Review chart vs Genius title/artist for correct matches',
  all: 'All resolution rows',
}

export default function PgpGeniusPage() {
  const [status, setStatus] = useState('unresolved')

  const fetchData = useCallback(() => fetchPgpSongMappings({ status }), [status])

  const showingMatched = status === 'matched' || status === 'all'

  const hiddenColumns = useMemo(() => {
    const hidden = ['ID']
    // Unmatched rows have no Genius side yet — those columns would be all blank.
    if (status === 'unresolved') {
      hidden.push('Genius Title', 'Genius Primary Artist', 'Genius Song ID', 'Credits Fetched')
    }
    return hidden
  }, [status])

  const defaultSort = useMemo(
    () =>
      showingMatched
        ? [
            { column: 'Updated At', direction: 'desc' },
            { column: 'Chart Title', direction: 'asc' },
          ]
        : [
            { column: 'Last Search', direction: 'desc' },
            { column: 'Chart Title', direction: 'asc' },
          ],
    [showingMatched],
  )

  const extraActions = (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5 shadow-sm">
      {VIEW_OPTIONS.map((opt) => {
        const on = status === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => setStatus(opt.value)}
            className={[
              'rounded-md px-2.5 py-1.5 text-sm font-medium',
              on ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-50',
            ].join(' ')}
            title={VIEW_TITLES[opt.value]}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )

  return (
    <SummaryDashboard
      title="Genius"
      subtitle={
        showingMatched
          ? 'Compare chart title/artist to Genius match details'
          : 'Unmatched chart songs awaiting Genius resolution'
      }
      fetchData={fetchData}
      defaultSort={defaultSort}
      searchPlaceholder="Search chart or Genius title/artist…"
      extraActions={extraActions}
      layout="mapping"
      truncateColumns={['Chart Title', 'Chart Artist', 'Genius Title', 'Genius Primary Artist']}
      hiddenColumns={hiddenColumns}
      placeholderColumns={PLACEHOLDER_GENIUS_SONG_MAPPING}
      tableKey={`pgp-genius-${status}`}
    />
  )
}
